import React from "react";

/**
 * Tile — a lightweight inline card primitive for list rows, sub-grids, and
 * small composable card-shaped items inside larger sections. Heavier siblings
 * (SectionCard) are for top-level page cards; Tile is for the things inside
 * them: contact rows, sequence steps, template list items, stop-condition
 * tiles, etc.
 *
 * Composition:
 *   <Tile onClick={...} selected={isSelected}>
 *     <Tile.Leading><Icon /></Tile.Leading>
 *     <Tile.Body>
 *       <Tile.Title>List item name</Tile.Title>
 *       <Tile.Description>Short description</Tile.Description>
 *     </Tile.Body>
 *     <Tile.Trailing>
 *       <Badge>Active</Badge>
 *     </Tile.Trailing>
 *   </Tile>
 */

type Density = "compact" | "default" | "comfortable";

const PAD: Record<Density, string> = {
  compact: "p-2",
  default: "p-3",
  comfortable: "p-4",
};

export interface TileProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onClick"> {
  selected?: boolean;
  disabled?: boolean;
  density?: Density;
  onClick?: (e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => void;
  children?: React.ReactNode;
  className?: string;
}

const TileRoot = React.forwardRef<HTMLDivElement, TileProps>(function Tile(
  {
    selected = false,
    disabled = false,
    density = "default",
    onClick,
    children,
    className = "",
    ...rest
  },
  ref
) {
  const clickable = typeof onClick === "function" && !disabled;

  const base =
    "flex items-center gap-3 rounded-lg border bg-white transition-colors";
  const padding = PAD[density];

  const stateClasses = disabled
    ? "border-zinc-200 opacity-60 cursor-not-allowed"
    : selected
    ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5"
    : clickable
    ? "border-zinc-200 cursor-pointer hover:border-zinc-300 hover:bg-zinc-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/40"
    : "border-zinc-200";

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!clickable) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.(e);
    }
  };

  return (
    <div
      ref={ref}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-disabled={disabled || undefined}
      onClick={clickable ? (e) => onClick?.(e) : undefined}
      onKeyDown={clickable ? handleKeyDown : undefined}
      className={`${base} ${padding} ${stateClasses} ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
});

function Leading({
  children,
  className = "",
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex-shrink-0 flex items-center ${className}`}>
      {children}
    </div>
  );
}

function Body({
  children,
  className = "",
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return <div className={`flex-1 min-w-0 ${className}`}>{children}</div>;
}

function Title({
  children,
  className = "",
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`text-sm font-medium text-zinc-800 leading-snug truncate ${className}`}
    >
      {children}
    </div>
  );
}

function Description({
  children,
  className = "",
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`text-xs text-zinc-500 mt-0.5 leading-relaxed ${className}`}>
      {children}
    </div>
  );
}

function Trailing({
  children,
  className = "",
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex-shrink-0 flex items-center gap-2 ${className}`}>
      {children}
    </div>
  );
}

type TileComponent = typeof TileRoot & {
  Leading: typeof Leading;
  Body: typeof Body;
  Title: typeof Title;
  Description: typeof Description;
  Trailing: typeof Trailing;
};

const Tile = TileRoot as TileComponent;
Tile.Leading = Leading;
Tile.Body = Body;
Tile.Title = Title;
Tile.Description = Description;
Tile.Trailing = Trailing;

export default Tile;
